import { FILTER_TYPES } from "../dao/types";

/**
 * Default sorting mappings for each entity type
 * These define the default sort column and direction for overview screens
 */
export const DEFAULT_SORT_MAPPINGS = {
  [FILTER_TYPES.project]: "reference asc",
  [FILTER_TYPES.system_group]: "number asc",
  [FILTER_TYPES.system]: "number asc",
  [FILTER_TYPES.location]: "number asc",
  [FILTER_TYPES.equipment]: "reference asc",
  [FILTER_TYPES.section]: "equipment asc", // Default for general section overview
  [FILTER_TYPES.cable]: "cable_number asc",
  [FILTER_TYPES.cable_type]: "type asc",
  [FILTER_TYPES.cable_bundle]: "number asc",
  [FILTER_TYPES.route_point]: "number asc",
  [FILTER_TYPES.drum]: "number asc",
  [FILTER_TYPES.order]: "number asc",
};

/**
 * Context-aware sorting for sections based on parent context
 * When viewing sections within an equipment detail page, sort by section number
 * When viewing sections in general overview, sort by equipment reference
 */
export const SECTIONS_CONTEXT_SORT = {
  EQUIPMENT_DETAIL: "number asc",
  GENERAL_OVERVIEW: "equipment asc",
};

/**
 * Field name mappings for business logic exceptions
 * Maps sort field names to actual database field names
 * This handles cases where the sort string uses a different name than the actual field
 */
export const FIELD_NAME_MAPPINGS = {
  [FILTER_TYPES.equipment]: {
    reference: "reference2", // Equipment sorts by "reference" but field is "reference2"
  },
  [FILTER_TYPES.section]: {
    equipment: "equipment_reference2", // Section sorts by "equipment" but field is "equipment_reference2"
  },
};

/**
 * Get the default sort string for a given entity type
 * @param {string} entityType - The entity type from FILTER_TYPES
 * @param {object} options - Optional context for specific sorting rules
 * @param {object} options.parent - Parent context (e.g., equipment_id for sections)
 * @returns {string} The default sort string (e.g., "reference asc")
 */
export const getDefaultSort = (entityType, options = {}) => {
  const { parent } = options || {};

  // Special case for sections with context-aware sorting
  if (entityType === FILTER_TYPES.section && parent?.equipment_id) {
    return SECTIONS_CONTEXT_SORT.EQUIPMENT_DETAIL;
  }

  return DEFAULT_SORT_MAPPINGS[entityType] || "id asc";
};

/**
 * Get the default sortModel array with correct field names for a given entity type
 * This handles business logic exceptions where sort field names differ from database field names
 * @param {string} entityType - The entity type from FILTER_TYPES
 * @param {object} options - Optional context for specific sorting rules
 * @param {object} options.parent - Parent context (e.g., equipment_id for sections)
 * @returns {array} sortModel array e.g. [{field: "reference2", sort: "asc"}]
 */
export const getDefaultSortModel = (entityType, options = {}) => {
  const sortString = getDefaultSort(entityType, options);
  const [fieldName, sortDirection = "asc"] = sortString.split(" ");

  // Get the actual field name using business logic mappings
  const fieldMappings = FIELD_NAME_MAPPINGS[entityType];
  const actualFieldName = fieldMappings?.[fieldName] || fieldName;

  return [
    {
      field: actualFieldName,
      sort: sortDirection,
    },
  ];
};
